
import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';

// Memoized components for better performance
const ServiceCard = memo(({ service, saberMais }: { service: { icon: string; title: string; description: string; color: string }; saberMais: string }) => (
  <article className="group p-8 bg-white rounded-2xl border border-gray-100 hover:border-teal-200 hover:shadow-xl transition-all duration-300">
    <div className={`w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br ${service.color} mb-6`}>
      <i className={`${service.icon} text-2xl text-white`} aria-hidden="true"></i>
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
    <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
    <button className="text-teal-500 font-medium flex items-center gap-2 group-hover:gap-3 transition-all cursor-pointer whitespace-nowrap">
      {saberMais} <i className="ri-arrow-right-line" aria-hidden="true"></i>
    </button>
  </article>
));

const TestimonialCard = memo(({ testimonial }: { testimonial: { name: string; role: string; text: string; avatar: string } }) => (
  <article className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
    <div className="flex gap-1 mb-4" role="img" aria-label="5 estrelas">
      {[...Array(5)].map((_, i) => (
        <i key={i} className="ri-star-fill text-amber-400" aria-hidden="true"></i>
      ))}
    </div>
    <p className="text-gray-600 mb-6 leading-relaxed">"{testimonial.text}"</p>
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-full overflow-hidden">
        <img src={testimonial.avatar} alt={`Foto de ${testimonial.name}`} className="w-full h-full object-cover object-top" loading="lazy" />
      </div>
      <div>
        <div className="font-medium text-gray-900">{testimonial.name}</div>
        <div className="text-sm text-gray-500">{testimonial.role}</div>
      </div>
    </div>
  </article>
));

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language || 'pt');
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [bookingData, setBookingData] = useState({ service: '', date: '', guests: '2', phone: '' });
  const [bookingStatus, setBookingStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [selectedTour, setSelectedTour] = useState<number | null>(null);
  const [tourBooking, setTourBooking] = useState({ tour: '', schedule: '', date: '', guests: '2', name: '', phone: '', email: '' });
  const [tourBookingStatus, setTourBookingStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const changeLanguage = useCallback((lang: string) => {
    i18n.changeLanguage(lang);
    setCurrentLang(lang);
  }, [i18n]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setMobileMenuOpen(false);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setFormStatus('sending');
    try {
      const response = await fetch('https://readdy.ai/api/form/d60v98ccm4ll99gupbcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as Record<string, string>).toString(),
      });
      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBookingStatus('sending');
    try {
      const response = await fetch('https://readdy.ai/api/form/d60v98ccm4ll99gupbbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(bookingData as Record<string, string>).toString(),
      });
      if (response.ok) {
        setBookingStatus('success');
        setBookingData({ service: '', date: '', guests: '2', phone: '' });
        setTimeout(() => setBookingStatus('idle'), 5000);
      } else {
        setBookingStatus('error');
      }
    } catch {
      setBookingStatus('error');
    }
  };

  const handleTourBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTourBookingStatus('sending');
    try {
      const response = await fetch('https://readdy.ai/api/form/d60v98ccm4ll99gupbc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(tourBooking as Record<string, string>).toString(),
      });
      if (response.ok) {
        setTourBookingStatus('success');
        setTourBooking({ tour: '', schedule: '', date: '', guests: '2', name: '', phone: '', email: '' });
        setTimeout(() => {
          setTourBookingStatus('idle');
          setSelectedTour(null);
        }, 3000);
      } else {
        setTourBookingStatus('error');
      }
    } catch {
      setTourBookingStatus('error');
    }
  };

  const services = [
    {
      icon: 'ri-car-line',
      title: t('services.transfers.title'),
      description: t('services.transfers.desc'),
      color: 'from-amber-400 to-orange-500'
    },
    {
      icon: 'ri-mountain-line',
      title: t('services.levadas.title'),
      description: t('services.levadas.desc'),
      color: 'from-green-400 to-emerald-500'
    },
    {
      icon: 'ri-camera-line',
      title: t('services.foto.title'),
      description: t('services.foto.desc'),
      color: 'from-rose-400 to-pink-500'
    }
  ];

  const testimonials = [
    {
      name: t('testimonials.t1.name'),
      role: t('testimonials.t1.role'),
      text: t('testimonials.t1.text'),
      avatar: 'https://public.readdy.ai/ai/img_res/5319ce3677f3123d30e4a4e7e1289eba.jpg'
    },
    {
      name: t('testimonials.t2.name'),
      role: t('testimonials.t2.role'),
      text: t('testimonials.t2.text'),
      avatar: 'https://readdy.ai/api/search-image?query=happy%20female%20tourist%20portrait%20smiling%20outdoor%20natural%20lighting%20summer%20style%20vacation%20mood&width=100&height=100&seq=108&orientation=squarish'
    }
  ];

  const tours = [
    {
      id: 1,
      title: t('tours.tour1.title'),
      subtitle: t('tours.tour1.subtitle'),
      description: t('tours.tour1.desc'),
      highlights: ['Floresta Laurissilva', currentLang === 'pt' ? 'Miradouros panorâmicos' : 'Panoramic viewpoints'],
      duration: '4h30',
      maxPeople: 8,
      includes: [t('tours.transporte')],
      priceSmall: 150,
      priceLarge: 180,
      itinerary: [
        { step: 1, location: currentLang === 'pt' ? 'Saída do Funchal' : 'Departure from Funchal', description: currentLang === 'pt' ? 'Início da viagem' : 'Start of the trip' },
        { step: 2, location: 'Miradouro do Pico do Facho', description: currentLang === 'pt' ? 'Vista panorâmica sobre a costa' : 'Panoramic view over the coast' },
        { step: 3, location: 'Miradouro da Portela', description: currentLang === 'pt' ? 'Paisagens deslumbrantes' : 'Stunning landscapes' },
        { step: 4, location: currentLang === 'pt' ? 'Paragem no Porto da Cruz' : 'Stop at Porto da Cruz', description: currentLang === 'pt' ? 'Vila tradicional à beira-mar' : 'Traditional seaside village' },
        { step: 5, location: 'Miradouro do Guindaste', description: currentLang === 'pt' ? 'Vistas espetaculares' : 'Spectacular views' },
        { step: 6, location: currentLang === 'pt' ? 'Paragem em Santana' : 'Stop in Santana', description: currentLang === 'pt' ? 'Casas típicas de colmo' : 'Traditional thatched houses' },
        { step: 7, location: 'Miradouro da Rocha do Navio', description: currentLang === 'pt' ? 'Paisagem costeira única' : 'Unique coastal landscape' },
        { step: 8, location: currentLang === 'pt' ? 'Chegada ao Funchal' : 'Arrival in Funchal', description: currentLang === 'pt' ? 'Fim do tour' : 'End of tour' }
      ],
      images: [
        'https://static.readdy.ai/image/065a6b809b75ba89e8b1bd94402939b9/00c1e3ae2b35ada8a635ad53a10d42d5.jpeg',
        'https://static.readdy.ai/image/065a6b809b75ba89e8b1bd94402939b9/483eb4e55a387a2a0befe9b222183808.webp',
        'https://static.readdy.ai/image/065a6b809b75ba89e8b1bd94402939b9/42bde309a254765dfc78f47674dbabc5.jpeg'
      ]
    },
    {
      id: 2,
      title: t('tours.tour2.title'),
      subtitle: t('tours.tour2.subtitle'),
      description: t('tours.tour2.desc'),
      highlights: ['Santana', currentLang === 'pt' ? 'Miradouros' : 'Viewpoints', currentLang === 'pt' ? 'Vilas tradicionais' : 'Traditional villages'],
      duration: '4h30',
      maxPeople: 8,
      includes: [t('tours.transporte')],
      priceSmall: 150,
      priceLarge: 180,
      itinerary: [
        { step: 1, location: currentLang === 'pt' ? 'Saída do Funchal' : 'Departure from Funchal', description: currentLang === 'pt' ? 'Início da viagem' : 'Start of the trip' },
        { step: 2, location: 'Miradouro do Pináculo', description: currentLang === 'pt' ? 'Vista panorâmica' : 'Panoramic view' },
        { step: 3, location: 'Miradouro do Cristo Rei', description: currentLang === 'pt' ? 'Monumento icónico' : 'Iconic monument' },
        { step: 4, location: 'Miradouro do Rosário', description: currentLang === 'pt' ? 'Paisagens verdejantes' : 'Green landscapes' },
        { step: 5, location: 'Miradouro Francisco Álvares de Nóbrega', description: currentLang === 'pt' ? 'Vistas deslumbrantes' : 'Stunning views' },
        { step: 6, location: currentLang === 'pt' ? 'Paragem no Porto da Cruz' : 'Stop at Porto da Cruz', description: currentLang === 'pt' ? 'Vila tradicional à beira-mar' : 'Traditional seaside village' },
        { step: 7, location: 'Miradouro do Guindaste', description: currentLang === 'pt' ? 'Vistas espetaculares' : 'Spectacular views' },
        { step: 8, location: currentLang === 'pt' ? 'Paragem em Santana' : 'Stop in Santana', description: currentLang === 'pt' ? 'Casas típicas de colmo' : 'Traditional thatched houses' },
        { step: 9, location: currentLang === 'pt' ? 'Chegada ao Funchal' : 'Arrival in Funchal', description: currentLang === 'pt' ? 'Fim do tour' : 'End of tour' }
      ],
      images: [
        'https://static.readdy.ai/image/065a6b809b75ba89e8b1bd94402939b9/70ec5cce4f7061068f42ed291f177934.jpeg',
        'https://static.readdy.ai/image/065a6b809b75ba89e8b1bd94402939b9/6bec2ab6b3c2092197eae024043595ca.jpeg',
        'https://static.readdy.ai/image/065a6b809b75ba89e8b1bd94402939b9/d1595b3b75d7f1faa6210b827a707af9.jpeg'
      ]
    }
  ];

  const schedules = [
    { id: 'morning', label: t('tours.manha'), time: '9:00 - 13:30' },
    { id: 'afternoon', label: t('tours.tarde'), time: '14:00 - 18:30' }
  ];

  const navItems = ['inicio', 'sobre', 'tours', 'servicos', 'contacto'];

  return (
    <div className="min-h-screen bg-white font-sans relative overflow-hidden">
      {/* Navigation */}
      <header>
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`} role="navigation" aria-label="Navegação principal">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
              <div className="flex items-center gap-12">
                <h1>
                  <a href="#inicio" onClick={(e) => { e.preventDefault(); scrollToSection('inicio'); }}>
                    <img 
                      src="https://public.readdy.ai/ai/img_res/39f630d8-d355-44f7-8382-c4b1193a2cd1.png" 
                      alt="Plusativo - Tours e Transfers Madeira" 
                      title="Plusativo Tours Madeira"
                      className="h-12 md:h-16 lg:h-20 w-auto"
                    />
                  </a>
                </h1>
                <div className="hidden md:flex items-center gap-8">
                  {navItems.map((item) => (
                    <a
                      key={item}
                      href={`#${item}`}
                      onClick={(e) => { e.preventDefault(); scrollToSection(item); }}
                      className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
                      } ${activeSection === item ? (isScrolled ? 'text-teal-600' : 'text-white') : ''}`}
                    >
                      {item === 'inicio' ? t('nav.inicio') : item === 'sobre' ? t('nav.sobre') : item === 'tours' ? t('nav.tours') : item === 'servicos' ? t('nav.servicos') : t('nav.contacto')}
                    </a>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* Language Switcher */}
                <div className="flex items-center gap-1 bg-white/10 backdrop-blur-sm rounded-full p-1">
                  <button
                    onClick={() => changeLanguage('pt')}
                    aria-label="Mudar para Português"
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      currentLang === 'pt' 
                        ? 'bg-teal-500 text-white' 
                        : isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/80 hover:bg-white/20'
                    }`}
                  >
                    PT
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    aria-label="Switch to English"
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      currentLang === 'en' 
                        ? 'bg-teal-500 text-white' 
                        : isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/80 hover:bg-white/20'
                    }`}
                  >
                    EN
                  </button>
                </div>
                <a
                  href="#contacto"
                  onClick={(e) => { e.preventDefault(); scrollToSection('contacto'); }}
                  className={`hidden sm:inline-flex px-6 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    isScrolled 
                      ? 'bg-gray-900 text-white hover:bg-gray-800' 
                      : 'bg-white text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {t('nav.reservar')} <i className="ri-arrow-right-up-line ml-1" aria-hidden="true"></i>
                </a>
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full ${isScrolled ? 'bg-gray-100' : 'bg-white/20'} cursor-pointer`}
                  aria-label="Menu"
                  aria-expanded={mobileMenuOpen}
                >
                  <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl ${isScrolled ? 'text-gray-900' : 'text-white'}`} aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
              <div className="px-6 py-4 space-y-2">
                {navItems.map((item) => (
                  <a
                    key={item}
                    href={`#${item}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection(item); }}
                    className="block py-3 px-4 text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer"
                  >
                    {item === 'inicio' ? t('nav.inicio') : item === 'sobre' ? t('nav.sobre') : item === 'tours' ? t('nav.tours') : item === 'servicos' ? t('nav.servicos') : t('nav.contacto')}
                  </a>
                ))}
                <a
                  href="#contacto"
                  onClick={(e) => { e.preventDefault(); scrollToSection('contacto'); }}
                  className="block py-3 px-4 bg-teal-500 text-white text-center rounded-lg font-medium cursor-pointer"
                >
                  {t('nav.reservar')}
                </a>
              </div>
            </div>
          )}
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section id="inicio" className="relative min-h-screen flex items-center" aria-label="Secção principal">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=breathtaking%20madeira%20island%20landscape%20panoramic%20view%20green%20mountains%20ocean%20coastline%20dramatic%20cliffs%20sunny%20day%20vibrant%20colors%20wide%20angle%20cinematic%20photography&width=1920&height=1080&seq=110&orientation=landscape"
              alt="Paisagem deslumbrante da Ilha da Madeira com montanhas verdes e costa oceânica"
              title="Tours na Madeira - Paisagens Únicas"
              className="w-full h-full object-cover object-top"
              loading="eager"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full py-32">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
                  {t('hero.title1')} <span className="text-teal-400">{t('hero.title2')}</span> {t('hero.title3')}
                </h2>
                <p className="text-lg text-white/80 mb-10 leading-relaxed">
                  {t('hero.description')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="#servicos"
                    onClick={(e) => { e.preventDefault(); scrollToSection('servicos'); }}
                    className="px-8 py-4 bg-teal-500 text-white rounded-full font-medium hover:bg-teal-600 transition-all cursor-pointer whitespace-nowrap inline-flex items-center"
                  >
                    {t('hero.verServicos')} <i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
                  </a>
                  <a 
                    href="#contacto"
                    onClick={(e) => { e.preventDefault(); scrollToSection('contacto'); }}
                    className="px-8 py-4 bg-white/10 text-white border border-white/30 rounded-full font-medium hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm inline-flex items-center"
                  >
                    {t('hero.saberMais')}
                  </a>
                </div>
              </div>
              
              {/* Booking Form */}
              <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-calendar-check-line text-teal-400" aria-hidden="true"></i>
                  {t('booking.title')}
                </h3>
                {bookingStatus === 'success' ? (
                  <div className="p-6 bg-teal-500/20 border border-teal-400/30 rounded-2xl text-center" role="alert">
                    <i className="ri-checkbox-circle-line text-teal-400 text-4xl mb-3" aria-hidden="true"></i>
                    <p className="text-white font-medium">{t('booking.sucesso')}</p>
                    <p className="text-white/70 text-sm mt-2">{t('booking.sucessoMsg')}</p>
                  </div>
                ) : (
                  <form id="booking-form" data-readdy-form onSubmit={handleBookingSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="booking-service" className="block text-sm font-medium text-white/80 mb-2">{t('booking.tipoServico')}</label>
                      <select 
                        id="booking-service"
                        name="service"
                        value={bookingData.service}
                        onChange={(e) => setBookingData({...bookingData, service: e.target.value})}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                      >
                        <option value="" className="text-gray-900">{t('booking.selecioneServico')}</option>
                        <option value="Transfers" className="text-gray-900">{t('booking.transfers')}</option>
                        <option value="Levadas & Trilhos" className="text-gray-900">{t('booking.levadas')}</option>
                        <option value="Tours Fotográficos" className="text-gray-900">{t('booking.toursFoto')}</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="booking-date" className="block text-sm font-medium text-white/80 mb-2">{t('booking.data')}</label>
                        <input 
                          id="booking-date"
                          type="date" 
                          name="date"
                          value={bookingData.date}
                          onChange={(e) => setBookingData({...bookingData, date: e.target.value})}
                          required
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                        />
                      </div>
                      <div>
                        <label htmlFor="booking-guests" className="block text-sm font-medium text-white/80 mb-2">{t('booking.pessoas')}</label>
                        <select 
                          id="booking-guests"
                          name="guests"
                          value={bookingData.guests}
                          onChange={(e) => setBookingData({...bookingData, guests: e.target.value})}
                          required
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                        >
                          {[1,2,3,4,5,6,7,8,9,10].map(n => (
                            <option key={n} value={n} className="text-gray-900">{n} {n === 1 ? t('booking.pessoa') : t('booking.pessoasPlural')}</option>
                          ))}
                          <option value="10+" className="text-gray-900">10+ {t('booking.pessoasPlural')}</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="booking-phone" className="block text-sm font-medium text-white/80 mb-2">{t('booking.telefone')}</label>
                      <input 
                        id="booking-phone"
                        type="tel" 
                        name="phone"
                        value={bookingData.phone}
                        onChange={(e) => setBookingData({...bookingData, phone: e.target.value})}
                        required
                        placeholder="+351 912 345 678"
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <button 
                      type="submit"
                      disabled={bookingStatus === 'sending'}
                      className="w-full py-4 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {bookingStatus === 'sending' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                          {t('booking.aProcessar')}
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" aria-hidden="true"></i>
                          {t('booking.pedirReserva')}
                        </>
                      )}
                    </button>
                    {bookingStatus === 'error' && (
                      <div className="p-3 bg-red-500/20 border border-red-400/30 rounded-xl text-center text-white text-sm" role="alert">
                        {t('booking.erro')}
                      </div>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-white/60 text-2xl" aria-hidden="true"></i>
          </div>
        </section>

        {/* About Section */}
        <section id="sobre" className="py-24 bg-stone-50" aria-labelledby="about-title">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="w-full h-[500px] rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20tour%20guide%20in%20madeira%20island%20with%20tourists%20group%20hiking%20levada%20trail%20lush%20green%20nature%20sunny%20day%20friendly%20atmosphere&width=600&height=750&seq=111&orientation=portrait"
                    alt="Guia turístico profissional com grupo de turistas nas levadas da Madeira"
                    title="Tours guiados na Madeira"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-teal-500 text-white p-6 rounded-2xl shadow-xl">
                  <div className="text-4xl font-bold">15+</div>
                  <div className="text-sm opacity-90">{t('about.anos')}</div>
                </div>
              </div>
              <div>
                <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                  {t('about.tag')}
                </span>
                <h2 id="about-title" className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  {t('about.title1')} <span className="text-teal-500">{t('about.title2')}</span> {t('about.title3')}
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {t('about.p1')}
                </p>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {t('about.p2')}
                </p>
                <div className="grid grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                    <div className="text-3xl font-bold text-teal-500">5000+</div>
                    <div className="text-sm text-gray-500">{t('about.turistas')}</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                    <div className="text-3xl font-bold text-teal-500">98%</div>
                    <div className="text-sm text-gray-500">{t('about.satisfacao')}</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                    <div className="text-3xl font-bold text-teal-500">50+</div>
                    <div className="text-sm text-gray-500">{t('about.destinos')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="servicos" className="py-24 bg-white" aria-labelledby="services-title">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                {t('services.tag')}
              </span>
              <h2 id="services-title" className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t('services.title1')} <span className="text-teal-500">{t('services.title2')}</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {t('services.description')}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <ServiceCard key={index} service={service} saberMais={t('services.saberMais')} />
              ))}
            </div>
          </div>
        </section>

        {/* Tours Section */}
        <section id="tours" className="py-24 bg-stone-50" aria-labelledby="tours-title">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                {t('tours.tag')}
              </span>
              <h2 id="tours-title" className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t('tours.title1')} <span className="text-teal-500">{t('tours.title2')}</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {t('tours.description')}
              </p>
            </div>

            <div className="space-y-16">
              {tours.map((tour, index) => (
                <article 
                  key={tour.id}
                  className={`grid lg:grid-cols-2 gap-10 items-start ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                >
                  {/* Images Gallery */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <div className="h-64 rounded-2xl overflow-hidden shadow-lg">
                          <img 
                            src={tour.images[0]} 
                            alt={`${tour.title} - Imagem principal`}
                            title={tour.title}
                            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                      </div>
                      <div className="h-40 rounded-2xl overflow-hidden shadow-lg">
                        <img 
                          src={tour.images[1]} 
                          alt={`${tour.title} - Vista 2`}
                          className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="h-40 rounded-2xl overflow-hidden shadow-lg">
                        <img 
                          src={tour.images[2]} 
                          alt={`${tour.title} - Vista 3`}
                          className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tour Details */}
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium mb-4">
                      {tour.subtitle}
                    </span>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{tour.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{tour.description}</p>

                    {/* Highlights */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {tour.highlights.map((highlight, i) => (
                        <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm flex items-center gap-1.5">
                          <i className="ri-check-line text-teal-500" aria-hidden="true"></i>
                          {highlight}
                        </span>
                      ))}
                    </div>

                    {/* Itinerary */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-6">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <a href="#" className="hover:text-teal-500 transition-colors">
                          <i className="ri-route-line text-teal-500" aria-hidden="true"></i>
                          {t('tours.itinerario')}
                        </a>
                      </h4>
                      <div className="space-y-3">
                        {tour.itinerary.map((item, i) => (
                          <div key={i} className="flex gap-3">
                            <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-semibold">
                              {item.step}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-sm">{item.location}</div>
                              <div className="text-xs text-gray-500">{item.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="p-4 bg-white rounded-xl text-center shadow-sm">
                        <i className="ri-time-line text-teal-500 text-xl mb-1" aria-hidden="true"></i>
                        <div className="text-sm text-gray-500">{t('tours.duracao')}</div>
                        <div className="font-semibold text-gray-900">{tour.duration}</div>
                      </div>
                      <div className="p-4 bg-white rounded-xl text-center shadow-sm">
                        <i className="ri-group-line text-teal-500 text-xl mb-1" aria-hidden="true"></i>
                        <div className="text-sm text-gray-500">{t('tours.grupo')}</div>
                        <div className="font-semibold text-gray-900">{t('tours.ate')} {tour.maxPeople} {t('booking.pessoasPlural')}</div>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-6">
                      <div className="text-sm text-gray-500 mb-3">{t('tours.precos')}</div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">{t('tours.ate4')}</span>
                          <span className="text-2xl font-bold text-teal-600">{tour.priceSmall}€</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">{t('tours.5a8')}</span>
                          <span className="text-2xl font-bold text-teal-600">{tour.priceLarge}€</span>
                        </div>
                      </div>
                    </div>

                    {/* Includes */}
                    <div className="flex items-center gap-4 mb-6 text-sm text-gray-600">
                      <span className="font-medium">{t('tours.inclui')}</span>
                      {tour.includes.map((item, i) => (
                        <span key={i} className="flex items-center gap-1">
                          <i className="ri-checkbox-circle-fill text-teal-500" aria-hidden="true"></i>
                          {item}
                        </span>
                      ))}
                    </div>

                    {/* Schedule Options */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <a href="#" className="hover:text-teal-500 transition-colors">
                          <i className="ri-calendar-schedule-line text-teal-500" aria-hidden="true"></i>
                          {t('tours.escolhaHorario')}
                        </a>
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        {schedules.map((schedule) => (
                          <button
                            key={schedule.id}
                            onClick={() => {
                              setSelectedTour(tour.id);
                              setTourBooking({ ...tourBooking, tour: tour.title, schedule: `${schedule.label} (${schedule.time})` });
                            }}
                            className="p-4 border-2 border-gray-200 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all cursor-pointer group"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-900 group-hover:text-teal-600">{schedule.label}</span>
                              <i className="ri-sun-line text-amber-500" aria-hidden="true"></i>
                            </div>
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <i className="ri-time-line" aria-hidden="true"></i>
                              {schedule.time}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Tour Booking Modal */}
        {selectedTour && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="modal-title">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 id="modal-title" className="text-2xl font-bold text-gray-900">{t('tourModal.title')}</h3>
                <button 
                  onClick={() => setSelectedTour(null)}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
                  aria-label="Fechar"
                >
                  <i className="ri-close-line text-xl" aria-hidden="true"></i>
                </button>
              </div>

              {tourBookingStatus === 'success' ? (
                <div className="text-center py-8" role="alert">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-teal-500 text-3xl" aria-hidden="true"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{t('tourModal.sucesso')}</h4>
                  <p className="text-gray-600">{t('tourModal.sucessoMsg')}</p>
                </div>
              ) : (
                <form id="tour-booking-form" data-readdy-form onSubmit={handleTourBookingSubmit} className="space-y-5">
                  <div className="p-4 bg-teal-50 rounded-xl">
                    <div className="font-semibold text-teal-700">{tourBooking.tour}</div>
                    <div className="text-sm text-teal-600 flex items-center gap-1 mt-1">
                      <i className="ri-time-line" aria-hidden="true"></i>
                      {tourBooking.schedule}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="tour-name" className="block text-sm font-medium text-gray-700 mb-2">{t('tourModal.nome')}</label>
                    <input 
                      id="tour-name"
                      type="text"
                      name="name"
                      value={tourBooking.name}
                      onChange={(e) => setTourBooking({...tourBooking, name: e.target.value})}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder={t('tourModal.nomePlaceholder')}
                    />
                  </div>

                  <div>
                    <label htmlFor="tour-email" className="block text-sm font-medium text-gray-700 mb-2">{t('tourModal.email')}</label>
                    <input 
                      id="tour-email"
                      type="email"
                      name="email"
                      value={tourBooking.email}
                      onChange={(e) => setTourBooking({...tourBooking, email: e.target.value})}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="seu@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="tour-phone" className="block text-sm font-medium text-gray-700 mb-2">{t('tourModal.telefone')}</label>
                    <input 
                      id="tour-phone"
                      type="tel"
                      name="phone"
                      value={tourBooking.phone}
                      onChange={(e) => setTourBooking({...tourBooking, phone: e.target.value})}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="+351 912 345 678"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="tour-date" className="block text-sm font-medium text-gray-700 mb-2">{t('booking.data')}</label>
                      <input 
                        id="tour-date"
                        type="date"
                        name="date"
                        value={tourBooking.date}
                        onChange={(e) => setTourBooking({...tourBooking, date: e.target.value})}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                      />
                    </div>
                    <div>
                      <label htmlFor="tour-guests" className="block text-sm font-medium text-gray-700 mb-2">{t('booking.pessoas')}</label>
                      <select
                        id="tour-guests"
                        name="guests"
                        value={tourBooking.guests}
                        onChange={(e) => setTourBooking({...tourBooking, guests: e.target.value})}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                      >
                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                          <option key={n} value={n}>{n} {n === 1 ? t('booking.pessoa') : t('booking.pessoasPlural')}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <input type="hidden" name="tour" value={tourBooking.tour} />
                  <input type="hidden" name="schedule" value={tourBooking.schedule} />

                  <button 
                    type="submit"
                    disabled={tourBookingStatus === 'sending'}
                    className="w-full py-4 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {tourBookingStatus === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                        {t('booking.aProcessar')}
                      </>
                    ) : (
                      <>
                        <i className="ri-calendar-check-line" aria-hidden="true"></i>
                        {t('tourModal.confirmar')}
                      </>
                    )}
                  </button>

                  {tourBookingStatus === 'error' && (
                    <div className="p-3 bg-red-100 text-red-700 rounded-xl text-center text-sm" role="alert">
                      {t('booking.erro')}
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
        )}

        {/* Testimonials Section */}
        <section className="py-24 bg-stone-50" aria-labelledby="testimonials-title">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                {t('testimonials.tag')}
              </span>
              <h2 id="testimonials-title" className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t('testimonials.title1')} <span className="text-teal-500">{t('testimonials.title2')}</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <TestimonialCard key={index} testimonial={testimonial} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-32" aria-label="Chamada para ação">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=madeira%20island%20sunset%20golden%20hour%20dramatic%20sky%20ocean%20view%20coastal%20landscape%20warm%20colors%20cinematic%20wide%20angle%20photography&width=1920&height=800&seq=112&orientation=landscape"
              alt="Pôr do sol na Madeira com vista para o oceano"
              title="Reserve o seu tour na Madeira"
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/60"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
              {t('cta.description')}
            </p>
            <a 
              href="#contacto"
              onClick={(e) => { e.preventDefault(); scrollToSection('contacto'); }}
              className="px-10 py-4 bg-teal-500 text-white rounded-full font-medium text-lg hover:bg-teal-600 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-3"
            >
              <i className="ri-calendar-line text-xl" aria-hidden="true"></i>
              {t('cta.reservar')}
              <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
            </a>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contacto" className="py-24 bg-white" aria-labelledby="contact-title">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                  {t('contact.tag')}
                </span>
                <h2 id="contact-title" className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {t('contact.title1')} <span className="text-teal-500">{t('contact.title2')}</span> {t('contact.title3')}
                </h2>
                <p className="text-gray-600 mb-10 leading-relaxed">
                  {t('contact.description')}
                </p>
                <address className="space-y-6 not-italic">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                      <i className="ri-mail-line text-teal-600 text-xl" aria-hidden="true"></i>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">{t('contact.email')}</div>
                      <a href="mailto:Plusativo@hotmail.com" className="font-medium text-gray-900 hover:text-teal-600 transition-colors">Plusativo@hotmail.com</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                      <i className="ri-phone-line text-teal-600 text-xl" aria-hidden="true"></i>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">{t('contact.telefone')}</div>
                      <a href="tel:+351926655558" className="font-medium text-gray-900 hover:text-teal-600 transition-colors">+351 926 655 558</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                      <i className="ri-map-pin-line text-teal-600 text-xl" aria-hidden="true"></i>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">{t('contact.localizacao')}</div>
                      <div className="font-medium text-gray-900">Funchal, Ilha da Madeira</div>
                    </div>
                  </div>
                </address>
                <div className="flex gap-4 mt-10">
                  <a href="https://www.instagram.com/plusativo" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-full text-gray-600 hover:bg-teal-500 hover:text-white hover:border-teal-500 transition-all cursor-pointer" aria-label="Instagram">
                    <i className="ri-instagram-line" aria-hidden="true"></i>
                  </a>
                  <a href="https://www.facebook.com/share/188kDDNEF9/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-full text-gray-600 hover:bg-teal-500 hover:text-white hover:border-teal-500 transition-all cursor-pointer" aria-label="Facebook">
                    <i className="ri-facebook-fill" aria-hidden="true"></i>
                  </a>
                  <a href="https://wa.link/39h0xt" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-full text-gray-600 hover:bg-teal-500 hover:text-white hover:border-teal-500 transition-all cursor-pointer" aria-label="WhatsApp">
                    <i className="ri-whatsapp-line" aria-hidden="true"></i>
                  </a>
                </div>
              </div>
              <div className="bg-gray-50 p-8 md:p-10 rounded-3xl">
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.nome')}</label>
                    <input 
                      id="contact-name"
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder={t('contact.nomePlaceholder')}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.email')}</label>
                    <input 
                      id="contact-email"
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="seu@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.assunto')}</label>
                    <input 
                      id="contact-subject"
                      type="text" 
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder={t('contact.assuntoPlaceholder')}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.mensagem')}</label>
                    <textarea 
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value.slice(0, 500)})}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                      placeholder={t('contact.mensagemPlaceholder')}
                    ></textarea>
                    <div className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</div>
                  </div>
                  <button 
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full py-4 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'sending' ? t('contact.aEnviar') : t('contact.enviar')}
                  </button>
                  {formStatus === 'success' && (
                    <div className="p-4 bg-green-100 text-green-700 rounded-xl text-center" role="alert">
                      {t('contact.sucesso')}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-4 bg-red-100 text-red-700 rounded-xl text-center" role="alert">
                      {t('contact.erro')}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-stone-800 text-white py-16 rounded-t-[40px] mx-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <img 
                src="https://static.readdy.ai/image/065a6b809b75ba89e8b1bd94402939b9/2419f397015a039e5741928ac8302589.png" 
                alt="Plusativo Logo" 
                className="h-10 md:h-12 lg:h-14 w-auto mb-6 brightness-0 invert"
                loading="lazy"
              />
              <p className="text-gray-300 leading-relaxed max-w-sm">
                {t('footer.description')}
              </p>
            </div>
            <nav aria-label="Links rápidos">
              <h4 className="font-semibold mb-6">
                <a href="#inicio" className="hover:text-teal-400 transition-colors cursor-pointer">{t('footer.linksRapidos')}</a>
              </h4>
              <ul className="space-y-3">
                <li><a href="#inicio" className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">{t('nav.inicio')}</a></li>
                <li><a href="#sobre" className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">{t('nav.sobre')}</a></li>
                <li><a href="#tours" className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">{t('nav.tours')}</a></li>
                <li><a href="#servicos" className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">{t('nav.servicos')}</a></li>
                <li><a href="#contacto" className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">{t('nav.contacto')}</a></li>
              </ul>
            </nav>
            <nav aria-label="Redes sociais">
              <h4 className="font-semibold mb-6">
                <a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">{t('footer.redesSociais')}</a>
              </h4>
              <ul className="space-y-3">
                <li><a href="https://www.instagram.com/plusativo" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer flex items-center gap-2"><i className="ri-instagram-line" aria-hidden="true"></i> Instagram</a></li>
                <li><a href="https://www.facebook.com/share/188kDDNEF9/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer flex items-center gap-2"><i className="ri-facebook-fill" aria-hidden="true"></i> Facebook</a></li>
                <li><a href="https://wa.link/39h0xt" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer flex items-center gap-2"><i className="ri-whatsapp-line" aria-hidden="true"></i> WhatsApp</a></li>
              </ul>
            </nav>
          </div>
          <div className="border-t border-stone-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              {t('footer.direitos')}
            </p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
